/* Common Imports */

import React from "react";

/* Redux Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../redux-magic/state-interface";

/* Component Imports */

import { SvgIcon, Typography, styled, useTheme } from "@mui/material";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import BeegruButton from "../../common-components/buttons/BeegruButton";
import CreateNewBroadcastModal from "./CreateNewBroadcastModal";

/* Icons / Illustrations */

import { CampaignOutlined } from "@mui/icons-material";
import BroadCastBanner from "../../../public/images/broadcast/broadcast_banner.svg";

/* Styled Components */

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
	margin: "0rem 0rem 0rem 0rem",
}));

const BannerContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "locale",
})<{ locale: string }>(({ theme, locale }) => ({
	display: "flex",
	flexDirection: "column",
	direction: locale === "ar-AE" ? "rtl" : "ltr",
	justifyContent: "space-between",
	alignItems: "space-between",
	width: "100%",
	minHeight: "5.125rem",
	height: "auto",
	gap: "1rem",
	borderRadius: "16px",
	background: "#fff7f0",
	padding: "1rem 1rem 1rem 1rem",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	gap: "0.5rem",
}));

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	gap: "0.25rem",
}));

const Title = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	lineHeight: "1.25rem",
	color: theme.palette.mode === "dark" ? "#FFFFFF" : "#212121",
	fontWeight: 500,
}));

const SubTtext = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	lineHeight: "1.25rem",
}));

const BroadcastBannerPropertyUploadSuccess = ({
	session,
	dispatch,
	router,
	propertyStatus,
	business_profile_id,
	transactionType,
	propertyId,
	broadcastData,
	handleClose,
	broadcastFeatureDiscoveryTracker,
}: {
	session: any;
	dispatch: Function;
	router: NextRouter;
	propertyStatus: string;
	business_profile_id: string | ObjectId;
	transactionType: string;
	propertyId: ObjectId | string;
	broadcastData: StateInterface["broadcast_data"];
	handleClose: () => void;
	broadcastFeatureDiscoveryTracker: StateInterface["feature_discovery_tracker"]["broadcast"];
}) => {
	const theme = useTheme();

	const [open, setOpen] = React.useState<boolean>(false);

	/* Translations */

	const localLang = {
		broadcast_your_property_for_free:
			router.locale === "ar-AE"
				? "بث عقارك مجانًا"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಆಸ್ತಿಯನ್ನು ಉಚಿತವಾಗಿ ಪ್ರಸಾರಿಸಿ"
					: "Broadcast this property for FREE",
		banner_content:
			router.locale === "ar-AE"
				? "تواصل مع أكثر من ٣٥٠٠ وكيل على بيغرو وأتمم الصفقات بشكل أسرع"
				: router.locale === "kn-IN"
					? "ಬೀಚ್ರುನ 3500+ ಏಜೆಂಟ್‌ಗಳೊಂದಿಗೆ ಸಂಪರ್ಕ ಹೊಂದಿ ಮತ್ತು ಒಪ್ಪಂದಗಳನ್ನು ವೇಗವಾಗಿ ಮುಚ್ಚಿ"
					: "Connect with 3500+ agents on Beegru and close deals faster",
		broadcast_now:
			router.locale === "ar-AE" ? "بث الآن" : router.locale === "kn-IN" ? "ಈಗ ಪ್ರಸಾರ ಮಾಡಿ" : "Broadcast now",
	};

	return (
		<MainContainer>
			<CreateNewBroadcastModal
				broadcastFeatureDiscoveryTracker={broadcastFeatureDiscoveryTracker}
				source={{url: session.user.dbUser.pages[0]?.page_slug, component: "property_upload"}}
				router={router}
				setOpen={setOpen}
				open={open}
				propertyStatus={propertyStatus}
				session={session}
				profile_context={session.user.dbUser.profile_context}
				business_profile_id={business_profile_id}
				dispatch={dispatch}
				broadcast={broadcastData}
				transactionType={transactionType}
				propertyId={propertyId}
				handleCloseCreateModal={handleClose}
			/>
			<BannerContainer locale={router.locale ? router.locale : "en-IN"}>
				<RowContainer
					sx={{
						width: "100%",
						gap: "1rem",
					}}
				>
					<SvgIcon
						component={BroadCastBanner}
						inheritViewBox={false}
						viewBox={"0 0 69 61"}
						sx={{
							width: "6rem",
							height: "auto",
						}}
					/>

					<ColumnContainer
						sx={{
							width: "100%",
							gap: "0.5rem",
						}}
					>
						<Title
							variant="h6"
							sx={{
								fontWeight: router.locale === "ar-AE" ? 600 : 500,
							}}
						>
							{localLang.broadcast_your_property_for_free}
						</Title>

						<SubTtext
							sx={{
								textAlign: router.locale === "ar-AE" ? "right" : "left",
							}}
						>
							{localLang.banner_content}
						</SubTtext>
					</ColumnContainer>
				</RowContainer>

				<BeegruButton
					flavor="success"
					variant="contained"
					size="medium"
					sx={{
						"&.MuiButton-contained": {
							color: theme.palette.primary.contrastText,
							background: "#621b16",
							"&:hover": {
								background: "#621b16",
							},
							"&:active": {
								background: "#621b16",
							},
							"&:focus": {
								background: "#621b16",
							},
						},
					}}
					onClick={(e: any) => {
						// e.stopPropagation();
						setOpen(true);
					}}
					disableElevation
					disableRipple
					disableFocusRipple
					disableTouchRipple
					disableHoverRipple
					disableInteractive
					disableTouchListener
					disableFocusListener
				>
					{localLang.broadcast_now}
					<CampaignOutlined
						sx={{
							margin: "0rem 0.25rem 0rem 0.25rem",
						}}
					/>
				</BeegruButton>
			</BannerContainer>
		</MainContainer>
	);
};

export default BroadcastBannerPropertyUploadSuccess;
